import BaseLayer from 'ol/layer/Base';
import { Feature } from 'ol';
import { Geometry } from 'ol/geom';
import { Component, computed, effect, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatOptionModule } from '@angular/material/core';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSliderModule } from '@angular/material/slider';
import { Store } from '@ngrx/store';
import { scaleQuantile } from 'd3-scale';
import { FeatureLike } from 'ol/Feature';
import GeoJSON from 'ol/format/GeoJSON';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import Fill from 'ol/style/Fill';
import Stroke from 'ol/style/Stroke';
import Style from 'ol/style/Style';
import Text from 'ol/style/Text';
import { debounceTime, map } from 'rxjs';
import { AppService } from '../../../../services/app.service';
import { LayerService } from '../../../../services/layer.service';
import { LegendItem, LegendService } from '../../../../services/legend.service';
import { MapService } from '../../../../services/map.service';
import { RestorationService } from '../../../../services/restoration.service';
import { selectCounties } from '../../../../state/location/location.reducer';
import { BaseUILayer } from '../../../classes/layers/base-ui-layer';
import { LayerHeaderComponent } from '../../layer-header/layer-header.component';

interface RestorationPercent {
  column: string;
  percentValue: number;
}

interface QuantileBin {
  min: number;
  max: number;
  label: string;
  color: string;
}

@Component({
  selector: 'tgrid-outage-event-count-layer',
  imports: [
    FormsModule,
    MatExpansionModule,
    MatFormFieldModule,
    MatOptionModule,
    MatSelectModule,
    MatSlideToggleModule,
    MatSliderModule,
    ReactiveFormsModule,
    LayerHeaderComponent,
],
  templateUrl: './outage-event-count-layer.component.html',
  standalone: true,
  styleUrl: './outage-event-count-layer.component.scss',
})
export class OutageEventCountLayerComponent extends BaseUILayer<BaseLayer> {

  private restorationService = inject(RestorationService);
  private layerService = inject(LayerService);
  private legendService = inject(LegendService);
  private store = inject(Store);

  protected readonly restorationPercents = [
    { column: 'res_0', percentValue: 0 },
    { column: 'res_20', percentValue: 20 },
    { column: 'res_40', percentValue: 40 },
    { column: 'res_60', percentValue: 60 },
    { column: 'res_80', percentValue: 80 },
    { column: 'res_100', percentValue: 100 },
  ];

  private readonly binColors = ['#fdd0a2', '#fdae6b', '#fd8d3c', '#e6550d', '#a63603'];

  private currentBins = signal<QuantileBin[]>([]);

  protected legendItems = computed(() => {
    const bins = this.currentBins();
    const items: LegendItem[] = bins.map(bin => ({
      label: bin.label,
      color: bin.color,
    }));
    // Always add "No Data" at the end
    items.push({ label: 'No Data', color: '#444' });

    return items;
  });

  protected filterGroup = new FormGroup({
    percent: new FormControl<RestorationPercent>(this.restorationPercents[4], { nonNullable: true }),
    minDuration: new FormControl<number>(12, { nonNullable: true }),
  });

  private static readonly countyIdProp = 'geoid';

  private readonly selectedCountyIds = toSignal(
    this.store.select(selectCounties).pipe(
      map((counties) => counties.map(c => c.geoid)),
    ),
    { initialValue: [] },
  );

  constructor() {
    super();

    this.filterGroup.valueChanges.pipe(
      debounceTime(100),
      takeUntilDestroyed(),
    ).subscribe(() => this.getEventCount(this.selectedCountyIds()));

    effect(() => {
      this.appService.selectedStates();
      this.appService.selectedYears();
      if (this.checked) {
        this.getEventCount(this.selectedCountyIds());
      }
    });
  }

  override toggleLayer(checked: boolean): void {

    this.checked = checked;

    // If the toggle is turned off, remove the layer and exit
    if (!this.checked) {
      MapService.getMapRef()!.removeLayer(this.mapLayer().renderedLayer);
      this.legendService.clear(this.mapLayer().uiHandle);
      return;
    }

    // Set up the style function if this is the first time
    if (!this.layerService.styles.get(this.mapLayer().uiHandle)) {
      const styleLogic = (feature: FeatureLike, resolution: number) => {
        const properties = feature.getProperties() as { count?: number; };
        const count = properties.count;

        const color = () => {
          // No data case
          if (count === undefined) return '#444';

          // Find which bin this count falls into
          const bins = this.currentBins();
          for (const bin of bins) {
            if (count >= bin.min && count <= bin.max) {
              return bin.color;
            }
          }

          // Fallback (shouldn't happen, but just in case)
          return '#444';
        };

        return new Style({
          stroke: new Stroke({
            color: 'black',
            width: 2,
          }),
          fill: new Fill({
            color: color(),
          }),
          text: new Text({
            text: (resolution > 3000 && count !== undefined) ? '' : count?.toString(),
          }),
        });
      };

      this.layerService.styles.set(this.mapLayer().uiHandle, styleLogic);
    }

    this.getEventCount(this.selectedCountyIds());

  }

  override onDestroy(): void {
    this.legendService.clear(this.mapLayer().uiHandle);
  }

  override changeOpacity(opacity: number) {
    this.opacity = opacity;
    (this.mapLayer().renderedLayer as VectorLayer<VectorSource>).setOpacity(this.opacity / 100);
  }

  private appService = inject(AppService);
  private getEventCount(selectedCountyIds: readonly string[] = []): void {
    const filters = this.filterGroup.getRawValue();
    const states = this.appService.selectedStates();
    const years = this.appService.selectedYears();

    this.restorationService
      .getOutageEventCount(years, states, filters.percent.column, filters.minDuration)
      .pipe(
        map(featureCollection => {
          featureCollection.features = featureCollection.features.map((f: unknown) => {
            const feat = f as { properties: Record<string, unknown>; };

            if (feat.properties['fipsCode'] && !feat.properties['geoid']) {
              feat.properties['geoid'] = feat.properties['fipsCode'];
            }

            feat.properties['popoverData'] = this.mapLayer().createPopover(feat.properties);
            return feat;
          });
          return featureCollection;
        }),
      )
      .subscribe(featureCollection => {
        /* Calculate bins from the feature data */
        this.calculateAndSetBins(featureCollection.features as Array<{properties: Record<string, unknown>}>, selectedCountyIds);

        const features = new GeoJSON().readFeatures(featureCollection);
        const source = new VectorSource<Feature<Geometry>>({
          features: features as Feature<Geometry>[],
          attributions: this.mapLayer().buildAttribution(),
        });

        // If the layer has already been created, just update the drawn features
        if (this.mapLayer().renderedLayer && this.mapService.isLayerOnMap(this.mapLayer().renderedLayer)) {
          (this.mapLayer().renderedLayer as VectorLayer<VectorSource>).setSource(source);
          (this.mapLayer().renderedLayer as VectorLayer<VectorSource>).changed();
          return;
        }

        // Create the initial layer
        this.mapLayer().renderedLayer = new VectorLayer({
          source,
          properties: {
            name: this.mapLayer().displayName,
          },
          opacity: 0.8,
          style: this.layerService.styles.get(this.mapLayer().uiHandle),
        });

        MapService.getMapRef()!.addLayer(this.mapLayer().renderedLayer);
      });
  }

  /**
     * This takes the raw feature data and creates quantile-based bins
     * @param features - The GeoJSON features from the API
     * @param selectedCountyIds - Currently selected county IDs (if any)
     */
  private calculateAndSetBins(
    features: Array<{ properties: Record<string, unknown>; }>,
    selectedCountyIds: readonly string[],
  ): void {
    if (!features || !Array.isArray(features) || features.length === 0) {
      this.currentBins.set([]);
      this.legendService.set(this.mapLayer().uiHandle, [{ label: 'No Data', color: '#444' }]);
      return;
    }

    const idKey = OutageEventCountLayerComponent.countyIdProp;
    const selected = new Set(selectedCountyIds);
    const shouldFilter = selected.size > 0;

    // filtering logic: Use *only* selected counties; if none selected, treat as "all"
    const inSelection = (p: Record<string, unknown>) => {

      if (!shouldFilter) return true;
      const id = p[idKey];
      return typeof id === 'string' && selected.has(id);
    };

    //extract all count values
    const counts: number[] = features
      .map(f => f.properties)
      .filter(inSelection)
      .map(p => p['count'])
      .filter((v): v is number => typeof v === 'number' && !isNaN(v));

    // Handle edge case: no data
    if (counts.length === 0) {
      this.currentBins.set([]);
      this.legendService.set(this.mapLayer().uiHandle, [{ label: 'No Data', color: '#444' }]);
      return;
    }

    const scale = scaleQuantile<string>()
      .domain(counts)
      .range(this.binColors);

    // Get the threshold values that separate each quantile
    const quantiles = scale.quantiles();
    const bins: QuantileBin[] = [];
    const minValue = Math.min(...counts);
    const maxValue = Math.max(...counts);

    // build the bins using the quantile thresholds
    for (let i = 0; i < this.binColors.length; i++) {
      const min = i === 0 ? minValue : Math.ceil(quantiles[i - 1]);
      const max = i === this.binColors.length - 1 ? maxValue : Math.floor(quantiles[i]);

      const label = `${min}–${max}`;

      bins.push({
        min,
        max,
        label,
        color: this.binColors[i],
      });
    }

    this.currentBins.set(bins);
    this.legendService.set(this.mapLayer().uiHandle, this.legendItems());
  }

  public exportDataAsCsv(event: MouseEvent): void {
    event.stopPropagation();
    this.mapLayer().downloadFeaturesAsCsv();
  }

}
